import { useEffect, useRef, useState } from 'react';
import styles from './HeroWaveform.module.css';

interface Props {
  /**
   * Returns the current normalized audio level (0..1) in enhanced mode,
   * or null when no analyser is wired (browser STT mode or pre-listen).
   * Polled per animation frame from inside the component; the hook
   * avoids a React re-render storm by reading a ref each frame.
   *
   * Same hook signature as AudioMeter so the call-site swap on the
   * home view is one prop-shape switch. The two components are
   * intentionally separate — AudioMeter serves the cross-surface
   * labelPill at the original 28×14 / 5-bar register; HeroWaveform
   * is bespoke to PrayPage's home view at 260×48 / mirrored sine.
   */
  getLevel: () => number | null;
}

const LIVE_FLOOR = 0.18;
const LIVE_GAIN = 6;
const LIVE_CEILING = 1;

/**
 * Static SVG path: a pure sine wave with 4 cycles across the 260×48
 * viewBox. Centerline at y=24; full-amplitude peaks at y=0 (top) and
 * y=48 (bottom). The `T` (smooth quadratic) command alternates control
 * direction automatically so a single path string traces the entire
 * sine. Period = 65px (260/4). Apple Voice Memos register — calm
 * rhythm without feeling slow or busy. Path geometry is fixed; the
 * waveform's response to amplitude is via scaleY on the wrapping <g>,
 * not via path regeneration. No rolling buffer needed (Variant 2 /
 * live-pulse design).
 */
const PATH_D =
  'M 0,24 Q 16.25,0 32.5,24 T 65,24 T 97.5,24 T 130,24 T 162.5,24 T 195,24 T 227.5,24 T 260,24';

/**
 * Hero waveform for PrayPage's home view listening state. Mirrored
 * continuous sine that flexes with current amplitude in real time.
 *
 * Two modes selected at runtime per the level returned by the
 * consumer:
 *   - `live` (level >= 0): `--wave-amp` CSS custom property set per
 *     frame from the analyser RMS, driving scaleY on the SVG group.
 *     Used in enhanced mode.
 *   - `synthetic` (level === null): CSS keyframe pulse drives scaleY
 *     between 0.18 and 0.30 over a 2s cycle. Used in browser STT mode
 *     where there's no audio source, and during the brief window
 *     between recorder start and first analyser data.
 *
 * The component never appears blank — the synthetic pulse is the
 * "trust mechanism" for closed-eyes prayer (same B.1 invariant
 * AudioMeter honors at its smaller register): even when no audio is
 * detected and no haptic fires (iOS), the wave keeps breathing so
 * the user perceives the session as alive.
 *
 * Per-frame work: 1 CSS variable write (vs AudioMeter's 22 transform
 * updates at 22 bars). Path string is static; geometry never
 * regenerates. GPU-cheap.
 *
 * Two-path glow approach (back stroke 5px / 0.4 alpha + foreground
 * stroke 2.5px / full alpha) sidesteps the K.3 modal-stacking bug
 * class — no `filter` keyframe, no `will-change`, pure GPU
 * compositing. See rule #20 + CLAUDE.md banked-items "Modal-backdrop
 * stacking workaround stays load-bearing" entry.
 */
export default function HeroWaveform({ getLevel }: Props) {
  const groupRef = useRef<SVGGElement | null>(null);
  const rafRef = useRef<number | null>(null);
  // Mode is presentation-only; we keep it in state so the data-mode
  // attribute updates and the CSS synthetic-pulse animation can
  // attach/detach. Most frames don't change mode so re-renders stay
  // rare.
  const [mode, setMode] = useState<'live' | 'synthetic'>('synthetic');

  useEffect(() => {
    let active = true;
    let lastMode: 'live' | 'synthetic' = 'synthetic';

    const tick = () => {
      if (!active) return;
      const level = getLevel();
      const next: 'live' | 'synthetic' = level === null ? 'synthetic' : 'live';
      if (next !== lastMode) {
        lastMode = next;
        setMode(next);
      }

      const el = groupRef.current;
      if (next === 'live' && level !== null && el) {
        const amp = Math.max(
          LIVE_FLOOR,
          Math.min(LIVE_CEILING, level * LIVE_GAIN + LIVE_FLOOR),
        );
        el.style.setProperty('--wave-amp', String(amp));
      } else if (el) {
        // Synthetic mode: clear the inline custom property so the CSS
        // keyframe animation drives scaleY instead of the variable.
        el.style.removeProperty('--wave-amp');
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      active = false;
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [getLevel]);

  return (
    <svg
      className={styles.svg}
      viewBox="0 0 260 48"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <g ref={groupRef} className={styles.group} data-mode={mode}>
        <path className={styles.glow} d={PATH_D} />
        <path className={styles.line} d={PATH_D} />
      </g>
    </svg>
  );
}
